import { useState } from 'react';
import {
  Alert,
  Button,
  Form,
  FormActions,
  FormSection,
  SelectField,
  SideSheet,
  TextField,
} from '@tessma/design-system';
import { refusalText } from '../ui/async.js';
import type { DuplicateCandidate, PartiesGateway } from '../parties/gateway.js';
import type { FinanceGateway } from './gateway.js';

/**
 * **ADD A CUSTOMER, OR A SUPPLIER, WITHOUT LEAVING THE DOCUMENT.**
 *
 * THE DEFECT THIS FIXES: raising an invoice for an organisation that was not
 * yet a customer meant abandoning the draft, going to Administration, finding
 * a screen headed "Party Master", registering the organisation, coming back
 * and starting the invoice again. In practice that is not what happens. What
 * happens is that somebody picks whichever existing customer is closest, and
 * the ledger acquires a debt owed by the wrong organisation.
 *
 * **ONE COMPONENT, BOTH ROLES.** Customer and supplier are the same two steps
 * in the same order — register the party if it is new, then give it a Finance
 * relationship with this entity — and the only differences are a word and
 * which endpoint the second step calls. Two components would be the same bug
 * fixed twice.
 *
 * **IT IS TWO CALLS BECAUSE THE PRODUCT IS TWO THINGS.** Identity belongs to
 * Party Master and is shared with CRM and everything after it; the terms and
 * the coding belong to Finance and are per legal entity. A single "create
 * customer" endpoint would put a Finance-shaped write on a Core table, which
 * is the shape of change this repository refuses. Neither call is new: both
 * have existed since Stage 8 and both are already used by screens elsewhere.
 *
 * **THE DUPLICATE CHECK IS ADVICE HERE AND A CONTROL ON THE SERVER.** Typing a
 * name asks the server who it looks like. Nothing here compares names —
 * "Ravenscroft Joinery Ltd" and "Ravenscroft Joinery Limited" are the same
 * organisation, and that is decided from a normalised registration number and
 * a trigram index by the server, which refuses the write with the same
 * candidates if somebody presses on regardless. A screen that only warned
 * would be a screen that could be skipped.
 */

export interface InlinePartyCreated {
  readonly partyId: string;
  readonly displayName: string;
}

export function AddPartySheet({
  role,
  open,
  onClose,
  finance,
  parties,
  legalEntityId,
  onCreated,
}: {
  readonly role: 'customer' | 'supplier';
  readonly open: boolean;
  readonly onClose: () => void;
  readonly finance: FinanceGateway;
  readonly parties: PartiesGateway;
  readonly legalEntityId: string;
  /** The caller selects this one and carries on with the document. */
  readonly onCreated: (created: InlinePartyCreated) => void;
}) {
  const noun = role === 'customer' ? 'customer' : 'supplier';
  const [name, setName] = useState('');
  const [identifierKind, setIdentifierKind] = useState<'company_number' | 'vat_number'>(
    'company_number',
  );
  const [identifier, setIdentifier] = useState('');
  const [taxCode, setTaxCode] = useState('');
  const [candidates, setCandidates] = useState<readonly DuplicateCandidate[]>([]);
  const [acknowledged, setAcknowledged] = useState<readonly string[]>([]);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | undefined>(undefined);

  function reset() {
    setName('');
    setIdentifier('');
    setTaxCode('');
    setCandidates([]);
    setAcknowledged([]);
    setProblem(undefined);
  }

  async function lookAlike(value: string) {
    setName(value);
    setAcknowledged([]);
    if (value.trim().length < 3) {
      setCandidates([]);
      return;
    }
    try {
      setCandidates(await parties.duplicates(value));
    } catch {
      // A FAILED LOOK-UP IS NOT A FAILED CREATE. The server refuses a genuine
      // duplicate on the write regardless, so a convenience that cannot reach
      // the server goes quiet rather than blocking the form.
      setCandidates([]);
    }
  }

  /** Give an EXISTING party the Finance relationship and hand it back. */
  async function relate(partyId: string, displayName: string) {
    await (role === 'customer'
      ? finance.saveCustomer({
          legalEntityId,
          partyId,
          ...(taxCode === '' ? {} : { defaultTaxCode: taxCode }),
        })
      : finance.saveSupplier({
          legalEntityId,
          partyId,
          ...(taxCode === '' ? {} : { defaultTaxCode: taxCode }),
        }));
    onCreated({ partyId, displayName });
    reset();
    onClose();
  }

  /**
   * THIS ONE IS ALREADY IN YOUR RECORDS.
   *
   * Offered as the FIRST action on a candidate, because using the record that
   * exists is almost always the right answer and creating a second one is
   * almost always the mistake this panel is here to prevent.
   */
  async function useExisting(candidate: DuplicateCandidate) {
    setSaving(true);
    setProblem(undefined);
    try {
      await relate(candidate.id, candidate.displayName);
    } catch (error) {
      setProblem(refusalText(error, `The ${noun} could not be added.`));
    } finally {
      setSaving(false);
    }
  }

  async function create() {
    setSaving(true);
    setProblem(undefined);
    try {
      const party = await parties.registerParty({
        partyKind: 'organisation',
        displayName: name,
        ...(identifier === ''
          ? {}
          : { identifiers: [{ kind: identifierKind, value: identifier }] }),
        roles: [role],
        // Only the candidates somebody explicitly said were different. An
        // empty list means the server's own check still applies in full.
        ...(acknowledged.length === 0 ? {} : { notADuplicateOf: acknowledged }),
      });
      await relate(party.id, party.displayName);
    } catch (error) {
      // THE SERVER'S SENTENCE, which names who this looks like. "Duplicate" on
      // its own leaves somebody with nothing to choose between.
      setProblem(refusalText(error, `The ${noun} could not be created.`));
    } finally {
      setSaving(false);
    }
  }

  return (
    <SideSheet
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title={role === 'customer' ? 'Add a customer' : 'Add a supplier'}
      subtitle={`They are added to your shared records and given a Finance relationship with this entity. You will come back to the ${
        role === 'customer' ? 'invoice' : 'bill'
      }.`}
    >
      {problem !== undefined ? <Alert tone="danger" title={problem} /> : null}
      <Form
        onSubmit={(event) => {
          event.preventDefault();
          void create();
        }}
      >
        <FormSection title="Who they are">
          <TextField
            label={role === 'customer' ? 'Customer name' : 'Supplier name'}
            value={name}
            onChange={(event) => {
              void lookAlike(event.target.value);
            }}
            helper="Their registered name, as it should appear on the document."
            required
          />
          <SelectField
            label="Identifier"
            value={identifierKind}
            onChange={(event) => {
              setIdentifierKind(event.target.value as 'company_number' | 'vat_number');
            }}
            options={[
              { value: 'company_number', label: 'Company number' },
              { value: 'vat_number', label: 'VAT number' },
            ]}
          />
          <TextField
            label="Identifier value"
            value={identifier}
            onChange={(event) => {
              setIdentifier(event.target.value);
            }}
            helper="Optional, and the surest way for the server to spot a duplicate."
          />
        </FormSection>

        {candidates.length > 0 && (
          <FormSection title="You may already have them">
            <Alert
              tone="warning"
              title={`The server found ${String(candidates.length)} organisation${
                candidates.length === 1 ? '' : 's'
              } with a similar name.`}
            >
              Using the record you already have keeps one history for them.
            </Alert>
            {candidates.map((candidate) => (
              <Alert
                key={candidate.id}
                tone="info"
                title={candidate.displayName}
                actions={
                  <>
                    <Button
                      variant="primary"
                      disabled={saving}
                      onClick={() => {
                        void useExisting(candidate);
                      }}
                    >
                      Use this one
                    </Button>
                    <Button
                      variant="secondary"
                      disabled={acknowledged.includes(candidate.id)}
                      onClick={() => {
                        setAcknowledged((current) => [...current, candidate.id]);
                      }}
                    >
                      {acknowledged.includes(candidate.id)
                        ? 'Marked as different'
                        : 'This is a different organisation'}
                    </Button>
                  </>
                }
              >
                {/* THE SERVER'S REASON, not a score this screen invented. */}
                {candidate.reason}
              </Alert>
            ))}
          </FormSection>
        )}

        <FormSection title="Finance defaults">
          <TextField
            label="Default tax code"
            value={taxCode}
            onChange={(event) => {
              setTaxCode(event.target.value);
            }}
            helper="Optional. Leave it blank to code each document as you enter it."
          />
        </FormSection>

        <FormActions
          submitLabel={role === 'customer' ? 'Add the customer' : 'Add the supplier'}
          submitting={saving}
          onCancel={() => {
            reset();
            onClose();
          }}
        />
      </Form>
    </SideSheet>
  );
}
